
import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/use-toast';
import { X, Send, CheckCircle, Wifi, WifiOff, RefreshCw } from 'lucide-react';

interface Comment {
  id: string;
  text: string;
  author: string;
  timestamp: string;
  resolved: boolean;
}

interface CommentsSectionProps {
  comments: Comment[];
  onAddComment: (text: string) => void;
  onToggleResolution: (commentId: string) => void;
  onClose: () => void;
}

const CommentsSection: React.FC<CommentsSectionProps> = ({
  comments,
  onAddComment,
  onToggleResolution,
  onClose
}) => {
  const [newComment, setNewComment] = useState('');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSaving, setIsSaving] = useState(false);
  
  // Update online status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      syncOfflineComments();
    };
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  
  // Sync offline comments when we come back online
  const syncOfflineComments = () => {
    try {
      const offlineComments = JSON.parse(localStorage.getItem('adaaltOfflineComments') || '[]');
      if (offlineComments.length > 0) {
        offlineComments.forEach(comment => {
          onAddComment(comment.text);
        });
        localStorage.removeItem('adaaltOfflineComments');
        toast({
          title: "Comments synced",
          description: `${offlineComments.length} offline comments have been synced.`
        });
      }
    } catch (error) {
      console.error('Error syncing offline comments:', error);
    }
  };

  const handleAddComment = () => {
    if (!newComment.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please enter a comment"
      });
      return;
    }
    
    setIsSaving(true);
    
    if (isOnline) {
      // If online, add comment directly
      onAddComment(newComment);
      setNewComment('');
    } else {
      // If offline, store comment locally
      try {
        const offlineComments = JSON.parse(localStorage.getItem('adaaltOfflineComments') || '[]');
        offlineComments.push({
          text: newComment,
          timestamp: new Date().toISOString()
        });
        localStorage.setItem('adaaltOfflineComments', JSON.stringify(offlineComments));
        toast({
          title: "Comment saved offline",
          description: "Your comment will be synced when you're back online"
        });
        setNewComment('');
      } catch (error) {
        console.error('Error saving offline comment:', error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to save offline comment"
        });
      }
    }
    
    setIsSaving(false);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Date(dateString).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Count offline comments
  const offlineCommentsCount = () => {
    try {
      const offlineComments = JSON.parse(localStorage.getItem('adaaltOfflineComments') || '[]');
      return offlineComments.length;
    } catch (error) {
      return 0;
    }
  };

  return (
    <Card className="h-full">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-md font-medium">Comments</CardTitle>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </Button>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col h-[400px]">
          {/* Online/Offline indicator */}
          <div className="flex items-center justify-between mb-2 text-xs">
            <div className="flex items-center">
              {isOnline ? (
                <div className="flex items-center text-green-600">
                  <Wifi size={14} className="mr-1" />
                  Online
                </div>
              ) : (
                <div className="flex items-center text-orange-500">
                  <WifiOff size={14} className="mr-1" />
                  Offline
                </div>
              )}
            </div>
            
            {!isOnline && offlineCommentsCount() > 0 && (
              <div className="text-orange-500 flex items-center">
                <span className="mr-1">{offlineCommentsCount()} unsaved</span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-6 w-6 p-0 text-orange-500"
                  onClick={syncOfflineComments}
                  disabled={!isOnline}
                >
                  <RefreshCw size={12} />
                </Button>
              </div>
            )}
          </div>
          
          <div className="flex-grow overflow-y-auto space-y-3 mb-4">
            {comments.length === 0 ? (
              <p className="text-sm text-center text-gray-500 py-8">
                No comments yet. Add the first comment!
              </p>
            ) : (
              comments.map((comment) => (
                <div 
                  key={comment.id} 
                  className={`p-3 rounded-md flex flex-col ${
                    comment.resolved ? 'bg-gray-100' : 'bg-primary/10'
                  }`}
                >
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm">{comment.author}</span>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-500">
                        {formatDate(comment.timestamp)}
                      </span>
                      <Button 
                        variant="ghost"
                        size="sm"
                        onClick={() => onToggleResolution(comment.id)}
                        className={`h-6 w-6 p-0 ${
                          comment.resolved ? 'text-green-600' : 'text-gray-400'
                        }`}
                      >
                        <CheckCircle className="h-4 w-4" />
                        <span className="sr-only">
                          {comment.resolved ? 'Mark as unresolved' : 'Mark as resolved'}
                        </span>
                      </Button>
                    </div>
                  </div>
                  <p className={`text-sm ${comment.resolved ? 'text-gray-500' : 'text-gray-900'}`}>
                    {comment.text}
                  </p>
                  {comment.resolved && (
                    <span className="text-xs text-green-600 mt-1 flex items-center">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Resolved
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
          
          <div className="border-t pt-3">
            <div className="flex gap-2">
              <Textarea
                placeholder="Add a comment..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                className="min-h-[80px] resize-none flex-grow"
              />
            </div>
            <div className="flex justify-end mt-2">
              <Button 
                onClick={handleAddComment}
                size="sm"
                className="flex items-center"
                disabled={isSaving || !newComment.trim()}
              >
                {isSaving ? (
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-b-transparent"></div>
                ) : (
                  <Send className="h-4 w-4 mr-1" />
                )}
                Send
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CommentsSection;
